import { FC } from 'react';
import { Tabs, Typography } from 'antd';
import { TbArmchair2, TbCalendarShare, TbCreditCard, TbFileInvoice, TbMailShare, TbPuzzle } from 'react-icons/tb';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useCASL } from '@/hooks/useCASL';
import { PlanCheckout } from '@/shared';
import ViewNotAccessibleBasedOnRole from '@/shared/components/layouts/views/ViewNotAccesibleBasedOnRole';
import { EMAIL_LINKS } from '@/shared/constants/externalLinks';
import { PERMISSIONS } from '@/shared/permissions';
import { useUserStore } from '@/stores';

import Metadata from './meta';
import Billing from './views/01-Billing';
import FeatureAddOns from './views/03-FeatureAddOns';
import Invoices from './views/05-Invoices';

const Plans: FC = (): JSX.Element => {
    const navigate = useNavigate();
    const url = '/settings/account/plans';

    const { selectedUser: user } = useUserStore();

    const onChangeTab = (newActiveKey: string) => {
        navigate(`${url}/${newActiveKey}`);
    };

    const { can } = useCASL();

    const items = [
        {
            label: (
                <>
                    <TbCreditCard size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }} />
                    Billing
                </>
            ),
            key: 'billing',
            children: can(PERMISSIONS.WORKSPACE.MANAGE) ? <Billing /> : <ViewNotAccessibleBasedOnRole />,
        },
        {
            label: (
                <>
                    <TbArmchair2 size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />
                    Seats
                </>
            ),
            key: 'seats',
            children: can(PERMISSIONS.WORKSPACE.MANAGE) ? (
                <PlanCheckout isOnboarding={false} />
            ) : (
                <ViewNotAccessibleBasedOnRole />
            ),
        },
        {
            label: (
                <>
                    <TbPuzzle size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }} />
                    Add-Ons
                </>
            ),
            key: 'addons',
            children: can(PERMISSIONS.WORKSPACE.MANAGE) ? <FeatureAddOns /> : <ViewNotAccessibleBasedOnRole />,
        },
        // {
        //     label: (
        //         <>
        //             <TbGauge size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }} />
        //             Credits
        //         </>
        //     ),
        //     key: 'credits',
        //     children: <Route path={`${path}/credits`} exact component={Usage} />,
        // },
        {
            label: (
                <>
                    <TbFileInvoice size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }} />
                    Invoices
                </>
            ),
            key: 'invoices',
            children: can(PERMISSIONS.WORKSPACE.MANAGE) ? <Invoices /> : <ViewNotAccessibleBasedOnRole />,
        },
    ];

    // !IMPORTANT: Items displayed based on where they are at (during onboarding, authenticated, free trial user seeing the TrialEnded view, etc.)
    const filteredItems = user?.onboardingCompleted === false ? items?.filter((i) => i?.key === 'seats') : items;

    const location = useLocation();

    const activeKey = location.pathname.split('/')[4];

    const { token } = useAntDesignTheme();

    // const [open, setOpen] = useState(false);

    return (
        <>
            <Metadata />
            <Typography.Title>Plans</Typography.Title>
            <Typography.Paragraph>
                <div>
                    <span style={{ color: token.colorTextTertiary }}>For enterprise plans, please</span>{' '}
                    <a href={EMAIL_LINKS.enterprise} style={{ color: token.blue5 }}>
                        contact us
                        <TbMailShare style={{ verticalAlign: '-0.175rem', marginLeft: '0.25rem' }} />
                    </a>{' '}
                    <span style={{ color: token.colorTextTertiary }}>or</span>{' '}
                    <a
                        href='http://cal.com/countercyclical/enterprise-demo'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: token.blue5 }}
                    >
                        schedule an enterprise demo
                        <TbCalendarShare style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                    </a>{' '}
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: token.colorTextTertiary }}>
                        Users on paid plans can also request access to beta features by{' '}
                    </span>
                    <a href={EMAIL_LINKS.engineering} style={{ color: token.blue5 }}>
                        contacting us
                        <TbMailShare style={{ verticalAlign: '-0.175rem', marginLeft: '0.25rem' }} />
                    </a>
                </div>
            </Typography.Paragraph>
            <Routes>
                <Route index element={<Navigate to={`${url}/billing`} replace />} />
                <Route
                    path='*'
                    element={
                        <>
                            <Tabs
                                items={filteredItems}
                                animated={true}
                                activeKey={activeKey}
                                size='small'
                                onChange={onChangeTab}
                                // tabBarExtraContent={
                                //     <Row>
                                //         <Col flex='none'>
                                //             <Button
                                //                 size='small'
                                //                 style={{ verticalAlign: '-0.25rem', padding: '0.25rem' }}
                                //                 onClick={() => setOpen(true)}
                                //             >
                                //                 <TbLicense style={{ verticalAlign: '-0.2rem' }} />
                                //                 Compare Plans
                                //             </Button>
                                //         </Col>
                                //     </Row>
                                // }
                            />
                            {/* <FeatureBreakdownModal open={open} setOpen={setOpen} /> */}
                        </>
                    }
                />
            </Routes>
        </>
    );
};

export default Plans;
